import css from './wordListItem.module.scss';

import { memo } from 'react';

import { useWordEditor } from 'com/WordEditor';
import { EXERCISE_LABELS } from 'core/helpers/deck';
import { ListPanel } from 'core/ui/ListPanel';
import { useMessageModal } from 'core/ui/MessageModal';
import ICONS from 'core/ui/icons';

import { Definitions, NoteRow, TextRow } from './Controls';
import MenuBody from './MenuBody';

type TProps<T> = {
  word: T;
  idx?: number;
  deck: TDeck;
  onChangeWord?: (word: TWord) => Promise<void> | void;
  onRemoveWord?: (wordUid: string) => Promise<void> | void;
};

const WordListItem = <T extends TWord | TWordUpdate>({
  deck,
  word,
  idx,
  onChangeWord,
  onRemoveWord,
}: TProps<T>) => {
  const { showError } = useMessageModal();

  const { editWord } = useWordEditor();

  const onEdit = async () => {
    try {
      if (!('uid' in word) || !onChangeWord) {
        throw new Error('TWordUpdate is not supported');
      }

      const editedWord = await editWord(word.uid);
      if (editedWord) {
        await onChangeWord(editedWord);
      }
    } catch (error) {
      showError(error);
    }
  };

  return (
    <ListPanel
      index={idx}
      body={
        <div className={css.bodyMeta}>
          <div className={css.definitionsAndNote}>
            <Definitions def={word.definitions} />
            {word.note && <NoteRow text={word.note} />}
          </div>
          <TextRow key="value" text={word.value} lang={deck.lang} />
          {word.valueNote && <NoteRow text={word.valueNote} />}
        </div>
      }
      exeStatuses={
        'uid' in word
          ? deck.exercises.map(exercise => ({
              label: EXERCISE_LABELS[exercise],
              lastProbeAt: word.status[exercise]?.lastProbeAt,
              nextProbeAt: word.status[exercise]?.nextProbeAt,
            }))
          : undefined
      }
      imageResources={[
        word.definitionResourceUid,
        word.valueResourceUid,
      ].filter((uid): uid is string => !!uid)}
      actions={[
        [
          {
            title: 'Edit',
            icon: ICONS.Edit,
            disabled: !onChangeWord,
            onClick: onEdit,
          },
        ],
        [
          {
            icon: ICONS.Menu,
            title: 'Show menu',
            disabled: !onChangeWord,
            menuItems: (
              <MenuBody deck={deck} word={word} onRemoveWord={onRemoveWord} />
            ),
          },
        ],
      ]}
    />
  );
};

export default memo(WordListItem) as typeof WordListItem;
